import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, ReplaySubject, Subscription } from "rxjs";
import { EndpointAccessType, EndpointModel, PolicyModel, PolicyToUpdateModel } from "src/app/models/api/governance/policy.model";
import { HttpErrorHandlerService } from "src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service";
import { NotificationsService } from "src/app/services/infra/notifications/notifications.service";
import { AddEndpointDialogComponent } from "../add-endpoint-dialog/add-endpoint-dialog.component";
import { IntegrationModel } from "src/app/models/api";
import { StatusCodeEnum } from "src/app/models/api/integration.model";
import { InactiveIntegrationDialogComponent } from "../inactive-integration-dialog/inactive-integration-dialog.component";
import { PoliciesHttpService } from "src/app/services/httpServices/governance/policiesHttpService/policies-http.service";
import { IEndpointsViewModel } from "src/app/models/viewModels/endpointsViewModel";
import { ITableColumn } from "src/app/models/tableColumns";
import { EndpointFilteringTableColumnsModel } from "src/app/models/tableColumns/endpointFilteringTableColumn.model";
import { EffectivePolicySettingEnum } from "src/app/services/infra/store/model/effectivePolicySetting.model";
import _ from "lodash";

@Component({
    selector: "app-policy-endpoint-filtering",
    templateUrl: "./policy-endpoint-filtering.component.html",
    styleUrls: ["./policy-endpoint-filtering.component.scss"],
    encapsulation: ViewEncapsulation.None,
    styles: [
        `
            :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
                position: -webkit-sticky;
                position: sticky;
                top: 0px;
            }

            @media screen and (max-width: 64em) {
                :host ::ng-deep .p-datatable .p-datatable-thead > tr > th {
                    top: 0px;
                }
            }
        `,
    ],
})
export class PolicyEndpointFilteringComponent implements OnInit, OnDestroy {
    @Input() public editable = true;
    @Input() public integrationModel: IntegrationModel;
    @Input() public policyModel: PolicyModel;
    @Input() public tableNotFoundText = "No results found";
    @Input() public tableNotFoundIcon = "assets/images/common/no-results-found.svg";
    @Output() public goBack: EventEmitter<void> = new EventEmitter<void>();
    @Output() public policyUpdated: EventEmitter<PolicyModel> = new EventEmitter<PolicyModel>();
    @Output() public notifyChanges: EventEmitter<boolean> = new EventEmitter<boolean>();

    public columns$ = new BehaviorSubject<ITableColumn[]>(EndpointFilteringTableColumnsModel);
    public resetSearchBoxBehavioralSubject$: ReplaySubject<string[] | undefined> = new ReplaySubject<string[] | undefined>();
    public endpointsCurrentPage$: BehaviorSubject<IEndpointsViewModel[]> = new BehaviorSubject<IEndpointsViewModel[]>([]);

    public EffectivePolicySettingEnum = EffectivePolicySettingEnum;

    public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public changesWereDone = false;

    public endpointsUrls: string[] = [];
    public enableRemoveEndpoint = false;

    private _policyConfigUpdateSubscription$?: Subscription;
    private _policyEntitySubscription$?: Subscription;

    private _endpointsViewModel: IEndpointsViewModel[] = [];
    private _endpointsSearchedViewModel: IEndpointsViewModel[] = [];

    constructor(
        private _matDialog: MatDialog,
        private _policiesHttpService: PoliciesHttpService,
        private _httpErrorHandlerService: HttpErrorHandlerService,
        private _notificationService: NotificationsService,
    ) {}

    ngOnInit(): void {
        this._endpointsViewModel = this.policyModel.config.endpoints.map((endpoint) => this._transformToEndpointsViewModel(endpoint));
        this.endpointsCurrentPage$.next(this._endpointsViewModel);
        this.endpointsUrls = this._endpointsViewModel.map((endpoint) => endpoint.url);
        this.enableRemoveEndpoint = this.endpointsUrls.length > 1;
    }

    ngOnDestroy(): void {
        this._policyConfigUpdateSubscription$?.unsubscribe();
        this._policyEntitySubscription$?.unsubscribe();
    }

    public cancelSettings() {
        this.goBack.emit();
    }

    public addEndpointClicked(): void {
        const current_policy = _.cloneDeep(this.policyModel);
        current_policy.config.endpoints = this._endpointsViewModel.map(
            (endpoint) =>
                ({
                    url: endpoint.url,
                    endpointAccess: endpoint.endpointAccess,
                }) as EndpointModel,
        );

        const dialogRef = this._matDialog.open(AddEndpointDialogComponent, {
            data: current_policy,
            disableClose: false,
            autoFocus: false,
            panelClass: "dynamic-size-popup-dialog-container",
        });
        dialogRef.afterClosed().subscribe((hostnames?: EndpointModel[]) => {
            if (hostnames) {
                this._endpointsViewModel.push(...hostnames.map((hostname) => this._transformToEndpointsViewModel(hostname)));
                this.endpointsCurrentPage$.next(this._endpointsViewModel);
                this.endpointsUrls = this._endpointsViewModel.map((endpoint) => endpoint.url);
                this.enableRemoveEndpoint = this.endpointsUrls.length > 1;
                this.changesWereDone = true;
                this.notifyChanges.emit(this.changesWereDone);
                this._resetSearchBox();
            }
        });
    }

    public resetForm(): void {
        this._endpointsViewModel = this.policyModel.config.endpoints.map((endpoint) => this._transformToEndpointsViewModel(endpoint));
        this.endpointsCurrentPage$.next(this._endpointsViewModel);
        this.endpointsUrls = this._endpointsViewModel.map((endpoint) => endpoint.url);
        this.enableRemoveEndpoint = this.endpointsUrls.length > 1;
        this._resetSearchBox();
        this.changesWereDone = false;
        this.notifyChanges.emit(this.changesWereDone);
    }

    public updatePolicyConfig() {
        this.isLoading$.next(true);
        if (this.integrationModel.connectionStatus.code == StatusCodeEnum.off) {
            const dialogRef = this._matDialog.open(InactiveIntegrationDialogComponent, {
                data: this.integrationModel,
                disableClose: false,
                panelClass: "dynamic-size-popup-dialog-container",
            });
            dialogRef.afterClosed().subscribe(() => {
                this._updatePolicyConfig();
            });
        } else {
            this._updatePolicyConfig();
        }
    }

    public removeEndpointClicked(endpointToRemove: IEndpointsViewModel): void {
        let index = this._endpointsViewModel.findIndex((x) => x.url === endpointToRemove.url);
        index > -1 ? this._endpointsViewModel.splice(index, 1) : null;
        index = this._endpointsSearchedViewModel.findIndex((x) => x.url === endpointToRemove.url);
        index > -1 ? this._endpointsSearchedViewModel.splice(index, 1) : null;

        // compare current endpoints and endpoints in policy
        const endpointsInPolicy = this.policyModel.config.endpoints.map((endpoint) => endpoint.url);
        const endpointsInCurrent = this._endpointsViewModel.map((endpoint) => endpoint.url);
        if (_.isEqual(endpointsInPolicy, endpointsInCurrent)) {
            this.changesWereDone = false;
            this.notifyChanges.emit(this.changesWereDone);
        } else {
            this.changesWereDone = true;
            this.notifyChanges.emit(this.changesWereDone);
        }
        this.endpointsUrls = this._endpointsViewModel.map((endpoint) => endpoint.url);
        this.enableRemoveEndpoint = this.endpointsUrls.length > 1;
    }

    public endpointSearched(endpoints: string[]) {
        this._endpointsSearchedViewModel = this._endpointsViewModel
            .map((endpoint) => this._transformToEndpointsViewModel(endpoint))
            .filter((endpoint) => endpoints.includes(endpoint.url));
        this.endpointsCurrentPage$.next(this._endpointsSearchedViewModel);
    }

    private _resetSearchBox() {
        this.endpointsUrls = this._endpointsViewModel.map((endpoint) => endpoint.url);
        this.resetSearchBoxBehavioralSubject$.next(this.endpointsUrls);
    }

    private _transformToEndpointsViewModel(endpoint: EndpointModel): IEndpointsViewModel {
        return {
            rowId: endpoint.url,
            url: endpoint.url,
            endpointAccess: endpoint.access ?? EndpointAccessType.allow,
            clickable: false,
        };
    }

    private _updatePolicyConfig() {
        // create the endpoints model
        const endpoints: EndpointModel[] = this._endpointsViewModel.map(
            (endpoint) =>
                ({
                    url: endpoint.url,
                    endpointAccess: endpoint.endpointAccess,
                }) as EndpointModel,
        );
        this.isLoading$.next(true);

        this._policyConfigUpdateSubscription$ = this._policiesHttpService
            .updatePolicy(this.integrationModel.id, this.policyModel.id, this._generateUpdatePolicyModel(endpoints))
            .subscribe({
                next: () => {
                    this._notificationService.success("Policy updated successfully");
                    this._policyEntitySubscription$ = this._policiesHttpService.getPolicy(this.integrationModel.id, this.policyModel.id).subscribe({
                        next: (response) => {
                            this.policyModel = response;
                            this.policyUpdated.emit(this.policyModel);
                            this._endpointsViewModel = this.policyModel.config.endpoints.map((endpoint) =>
                                this._transformToEndpointsViewModel(endpoint),
                            );
                            this.endpointsCurrentPage$.next(this._endpointsViewModel);
                        },
                        error: (error) => this._httpErrorHandlerService.handle(error),
                    });
                    this.changesWereDone = false;
                    this.notifyChanges.emit(this.changesWereDone);
                    this.isLoading$.next(false);
                },
                error: (error) => {
                    this._httpErrorHandlerService.handle(error);
                    this.resetForm();
                    this.isLoading$.next(false);
                },
            });
    }

    private _generateUpdatePolicyModel(endpoints: EndpointModel[]): PolicyToUpdateModel {
        const config = _.cloneDeep(this.policyModel.config);
        config.endpoints = endpoints;
        return {
            name: this.policyModel.name,
            description: this.policyModel.description,
            type: this.policyModel.isDefault ? undefined : this.policyModel.type,
            config: config,
        } as PolicyToUpdateModel;
    }
}
